import { SyntheticEvent, useState } from 'react';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { CoreBox, CoreButton, CoreTooltip, CoreTypography } from '@youscience/core';

import { DISCOVERY_URL } from '@constants/externalRoutes';

import { useSaveFavoriteCluster, useUnsaveFavoriteCluster } from '@hooks/Cluster';

import { Cluster } from '@services/cluster';

import { Divider } from '@mui/material';
import { StyledCard, StyledCardInfo, StyledContainerLink, StyledLogo, sxStyles } from './ClusterCard.styles';

export interface ClusterCardProps {
  cluster: Cluster;
  userId: string;
}

export const ClusterCard = ({ cluster, userId }: ClusterCardProps) => {
  const [saved, setSaved] = useState<boolean>(cluster?.isFavorite);
  const { mutateAsync: saveFavoriteCluster, isPending: isSaving } = useSaveFavoriteCluster(`${cluster.id}`, userId);
  const { mutateAsync: unsaveFavoriteCluster, isPending: isUnsaving } = useUnsaveFavoriteCluster(
    `${cluster.id}`,
    userId,
  );

  const handleToggleFavorite = async (event: SyntheticEvent) => {
    event.stopPropagation();

    if (isSaving || isUnsaving) return;

    setSaved((prevState) => !prevState);

    if (saved) {
      await unsaveFavoriteCluster();
    } else {
      await saveFavoriteCluster();
    }
  };

  const handleMoreDetails = () => {
    window.open(`${DISCOVERY_URL}/clusters/${cluster.id}`, '_blank');
  };

  const imageUrl = cluster.images.find((image) => image.type === 'illustration')?.url ?? cluster.images[0]?.url;

  return (
    <StyledContainerLink target="_blank" rel="noreferrer" data-testid="clusterCard" onClick={handleMoreDetails}>
      <StyledCard>
        <CoreBox sx={sxStyles.logoCover}>
          <StyledLogo src={imageUrl} alt={`${cluster.name} logo`} />
        </CoreBox>

        <StyledCardInfo>
          <CoreTooltip title={cluster.name} disableInteractive>
            <CoreTypography sx={sxStyles.title} variant="h5">
              {cluster.name}
            </CoreTypography>
          </CoreTooltip>

          <Divider />

          <CoreTypography sx={sxStyles.subtitle} variant="h6">
            Description
          </CoreTypography>

          <CoreTypography sx={sxStyles.description}>{cluster.description}</CoreTypography>

          <Divider />
        </StyledCardInfo>

        <CoreBox sx={sxStyles.containerButtons}>
          <CoreButton
            sx={sxStyles.favoriteBtn}
            variant="text"
            data-testid="toggleFavorite"
            onClick={handleToggleFavorite}
          >
            {saved ? <Favorite /> : <FavoriteBorder />}
          </CoreButton>
        </CoreBox>
      </StyledCard>
    </StyledContainerLink>
  );
};
